import type { CurrencyType } from "@/lib/gql/__generated__/graphql";
import { BillingTypeTarget } from "@/lib/gql/__generated__/graphql";

import { externalLink } from "@/routes/_constants/external-link";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForAutoGenerator, ForNodeType } from "@/components/features/_model/types/auto-generator-for-view-model";
import { QuestionSectionWrapper } from "@/components/features/items/edit/_shared/ui/components/system/question/_layouts/item-wrapper";

import type { FormOptionType } from "@/components/ui/form/_types/type";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { SelectField } from "@/components/ui/form/fields/select-field";

type Props = {
	isEdited: boolean;
	hasError: boolean;
	billingTypeTarget: ForNodeType<BillingTypeTarget>;
	price: ForAutoGenerator<
		{
			currencyType: CurrencyType;
			amount?: number | undefined;
		},
		""
	>;
	onSetBillingTypeTarget: (input: BillingTypeTarget) => void;
	onSetPriceAmount: (input: number | undefined) => void;
	/**
	 * 課金タイミングの表示。値は PER_DATE 固定。
	 * 宿泊は「１泊ごとに」、日帰りは「１日ごとに」。
	 */
	billingTypeTermLabel: string;
};

// 「宿泊を通して（PER_RESERVATION）」は2026年7月31日に廃止。保存値は PER_DATE のみ
const BILLING_TYPE_TERM_PER_RESERVATION_DEPRECATION_URL = `${externalLink.faqSite}/media/p/3d0fffe15c578162b17fd4cd6bbe420d`;

export const QuestionPlanPrice = ({
	isEdited,
	hasError,
	billingTypeTarget,
	onSetBillingTypeTarget,
	onSetPriceAmount,
	price,
	billingTypeTermLabel,
}: Props) => {
	const billingTypeTargetOptions = [
		{ id: "plan_billingTypeTarget_0", label: "１人あたり", value: BillingTypeTarget.Person },
		{ id: "plan_billingTypeTarget_1", label: "１部屋あたり", value: BillingTypeTarget.Room },
		{ id: "plan_billingTypeTarget_2", label: "１団体あたり", value: BillingTypeTarget.Group },
	] satisfies FormOptionType[];
	const handleBillingTypeTargetInput = (value: string) => {
		onSetBillingTypeTarget(value as BillingTypeTarget);
	};

	const handlePriceAmountInput = (value: number | undefined) => {
		onSetPriceAmount(value);
	};

	return (
		<FeatureIslandCategoryQuestionForMain
			required
			title="プラン料金"
			guide="この価格は部屋料金を含まないプラン単体の価格です。<br />予約時には別途、部屋料金が加算されます。"
			isEdited={isEdited}
			hasError={hasError}
			inputChildren={
				<div className="space-y-4">
					<p>部屋料金に追加するプラン料金が設定できます。プラン料金を追加しない場合は0円としてください。</p>
					<p className="text-error-600">
						※「宿泊を通して」は廃止となりました。 詳しくは
						<a
							href={BILLING_TYPE_TERM_PER_RESERVATION_DEPRECATION_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="font-bold underline underline-offset-4"
						>
							こちら
						</a>
					</p>
					<QuestionSectionWrapper>
						{/* 課金タイミングは PER_DATE 固定 */}
						<p>{billingTypeTermLabel}</p>
						<SelectField
							value={billingTypeTarget.value}
							options={billingTypeTargetOptions}
							errorMessage={billingTypeTarget.error}
							handleValueChange={handleBillingTypeTargetInput}
						/>
						<div className="flex items-center gap-x-2">
							<InputNumberField
								value={price.amount?.value}
								handleInput={handlePriceAmountInput}
								errorMessage={price.amount?.error}
							/>
							<p>円（税込）</p>
						</div>
					</QuestionSectionWrapper>
				</div>
			}
		/>
	);
};

QuestionPlanPrice.displayName = "QuestionPlanPrice";
